import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import HandyHome from '../Scenes/Home';
import TableSelector from '../Scenes/TableSelector';
import HandySettings from '../Scenes/Settings';
import HandyPrinters from '../Scenes/Settings/Printers';
import HandyPayments from '../Scenes/Settings/Payments';
import SimplePAT from '~Scenes/SimplePAT';
import HandyHistory from '../Scenes/History';
import Order from '../Scenes/History/Order';
import DeviceSettings from '../Scenes/Settings/Device';
import Developer from '~Scenes/Settings-v2/Scenes/Developer';
import GeneralSettings from '../Scenes/Settings/General';
import LanguageSettings from '../Scenes/Settings/General/Language';
import SafT from '../../Settings-v2/Scenes/SafT';
import EndOfDay from '../../../Scenes/EndOfDay';
import { STAFF_PERMISSIONS } from '../../../../__queries__';
import HandyQSR from '../Scenes/HandyQSR';
import POSLayout from '../Scenes/Settings/PosLayout';
import { useStaffCan } from '~Hooks';
import { useStaffContext } from './StaffProvider';
import EndOfDayWrapper from '../Scenes/EndOfDay';
import Competitions from '~Scenes/Competitions';
import KitchenTickets from '../Scenes/Settings/KitchenTickets';
import CryptoStandalone from '../Scenes/CryptoStandalone';
import ZReports from '~Scenes/Zreports';
import Reports from '~Scenes/Reports';
import StaffSettings from '../Scenes/Settings/StaffSettings';
import Standalone from '~Scenes/Standalone';
import HandyReceipts from '../Scenes/Settings/Receipts';
import OngoingChequePage from '../Scenes/History/OngoingCheque';
import ManagedPayments from '~Scenes/ManagedPayments';

export type PageNames =
  | 'home'
  | 'settings'
  | 'developer'
  | 'printerSettings'
  | 'kitchenTicketsSettings'
  | 'paymentSettings'
  | 'receiptSettings'
  | 'deviceSettings'
  | 'posLayoutSettings'
  | 'general'
  | 'languageSettings'
  | 'ordering'
  | 'simple-pat'
  | 'POS'
  | 'endOfDay'
  | 'safT'
  | 'competitions'
  | 'history'
  | 'history-order'
  | 'history-ongoing-cheque'
  | 'crypto'
  | 'z-reports'
  | 'reports'
  | 'standalone'
  | 'staffSettings'
  | 'managed-payments';

type Page = {
  name: PageNames;
  component: React.ReactNode;
  // order is used to determine the direction of the transition
  // if the page we are navigating to has a lower order than the current page, we are going right to left
  order: number;
  // Permissions required to even render the page. If not met, the user will be sent back to /home
  // Logic for this is handled in Handy/index.tsx
  // Home icons will be disabled based on this array as well
  permissions?: STAFF_PERMISSIONS[];
};

export const PAGES: { [key in PageNames]: Page } = {
  // Home
  home: {
    name: 'home',
    component: <HandyHome />,
    order: 0,
  },
  // Settings
  settings: {
    name: 'settings',
    component: <HandySettings />,
    order: 1,
    permissions: [STAFF_PERMISSIONS.SETTINGS],
  },
  developer: {
    name: 'settings',
    component: <Developer />,
    order: 2,
    permissions: [STAFF_PERMISSIONS.SETTINGS],
  },
  printerSettings: {
    name: 'printerSettings',
    component: <HandyPrinters />,
    order: 2,
    permissions: [STAFF_PERMISSIONS.SETTINGS],
  },
  kitchenTicketsSettings: {
    name: 'kitchenTicketsSettings',
    component: <KitchenTickets />,
    order: 3,
    permissions: [STAFF_PERMISSIONS.SETTINGS],
  },
  paymentSettings: {
    name: 'paymentSettings',
    component: <HandyPayments />,
    order: 2,
    permissions: [STAFF_PERMISSIONS.SETTINGS],
  },
  receiptSettings: {
    name: 'receiptSettings',
    component: <HandyReceipts />,
    order: 2,
    permissions: [STAFF_PERMISSIONS.SETTINGS],
  },
  posLayoutSettings: {
    name: 'posLayoutSettings',
    component: <POSLayout />,
    order: 2,
    permissions: [STAFF_PERMISSIONS.SETTINGS],
  },
  deviceSettings: {
    name: 'deviceSettings',
    component: <DeviceSettings />,
    order: 2,
    permissions: [STAFF_PERMISSIONS.SETTINGS],
  },
  general: {
    name: 'general',
    component: <GeneralSettings />,
    order: 2,
    permissions: [STAFF_PERMISSIONS.SETTINGS],
  },
  languageSettings: {
    name: 'languageSettings',
    component: <LanguageSettings />,
    order: 3,
    permissions: [STAFF_PERMISSIONS.SETTINGS],
  },
  staffSettings: {
    name: 'staffSettings',
    component: <StaffSettings />,
    order: 3,
    permissions: [STAFF_PERMISSIONS.SETTINGS],
  },
  // Ordering
  ordering: {
    name: 'ordering',
    component: <TableSelector />,
    order: 1,
    permissions: [STAFF_PERMISSIONS.ADD_ITEM_TO_TABLE],
  },
  POS: {
    name: 'POS',
    component: <HandyQSR />,
    order: 1,
  },
  // End of day
  endOfDay: {
    name: 'endOfDay',
    component: <EndOfDayWrapper />,
    order: 1,
    permissions: [STAFF_PERMISSIONS.CLOSE_DAY],
  },
  safT: {
    name: 'safT',
    component: <SafT isHandy />,
    order: 1,
    permissions: [STAFF_PERMISSIONS.SETTINGS],
  },
  // Competitions
  competitions: {
    name: 'competitions',
    component: <Competitions isHandy />,
    order: 1,
  },
  // Simple PAT
  'simple-pat': {
    name: 'simple-pat',
    component: <SimplePAT />,
    order: 1,
  },
  history: {
    name: 'history',
    component: <HandyHistory />,
    order: 2,
    permissions: [STAFF_PERMISSIONS.HISTORY],
  },
  'history-order': {
    name: 'history-order',
    component: <Order />,
    order: 3,
    permissions: [STAFF_PERMISSIONS.HISTORY],
  },
  'history-ongoing-cheque': {
    name: 'history-ongoing-cheque',
    component: <OngoingChequePage />,
    order: 3,
    permissions: [STAFF_PERMISSIONS.HISTORY],
  },
  crypto: {
    name: 'crypto',
    component: <CryptoStandalone />,
    order: 3,
  },
  'z-reports': {
    name: 'z-reports',
    component: <ZReports />,
    order: 2,
    permissions: [STAFF_PERMISSIONS.REPORTS],
  },
  standalone: {
    name: 'standalone',
    component: <Standalone />,
    order: 1,
  },
  reports: {
    name: 'reports',
    component: <Reports />,
    order: 2,
  },
  'managed-payments': {
    name: 'managed-payments',
    component: <ManagedPayments />,
    order: 1,
  },
};

interface ContextType {
  pageHistory: PageNames[];
  setPageHistory: Dispatch<SetStateAction<PageNames[]>>;
  current: PageNames;
  setCurrent: Dispatch<SetStateAction<PageNames>>;
  slug: string | null;
  setSlug: Dispatch<SetStateAction<string | null>>;
}

const Context = createContext<ContextType>({} as ContextType);

export type InitialPageProviderState = {
  current?: PageNames;
  slug?: string;
};

const PageProvider: React.FC<{ initialState?: InitialPageProviderState }> = ({
  initialState,
  children,
}) => {
  const [current, setCurrent] = useState<PageNames>(initialState?.current || 'home');
  const [slug, setSlug] = useState<string | null>(initialState?.slug || null);
  const [pageHistory, setPageHistory] = useState<PageNames[]>([current]);

  return (
    <Context.Provider
      value={{
        pageHistory,
        setPageHistory,
        current,
        setCurrent,
        slug,
        setSlug,
      }}>
      {children}
    </Context.Provider>
  );
};

export const usePage = () => {
  const context = React.useContext(Context);
  const { multi } = useStaffCan();
  const { authedStaff } = useStaffContext();

  if (!context.setCurrent) {
    throw new Error('usePage must be used within a PageProvider');
  }

  // PAGE PERMISSION CHECK
  useEffect(() => {
    // If no staff is logged in, do not check permissions. This will prevent rerouting when staff is automatically logged out
    if (!authedStaff) {
      return;
    }
    // Check permissions on current page, if logged in staff is missing permission go to home
    if (PAGES[context.current].permissions?.length) {
      if (!multi(PAGES[context.current].permissions!, true)) {
        navigateTo('home');
        context.setPageHistory([]);
      }
    }
  }, [context.current, authedStaff]);

  // Handle page transitions by setting the direction of the transition based on the order of the pages
  const navigateTo = (newPage: PageNames, slug?: string) => {
    const newPageOrder = PAGES[newPage].order;
    const currentPageOrder = PAGES[context.current].order;

    // If the new page is the previous page in history, just remove the last entry
    if (!!history.length && newPage === context.pageHistory[context.pageHistory.length - 2]) {
      context.setPageHistory((prev) => [...prev].slice(0, -1));
    } else {
      context.setPageHistory((prev) => [...prev, newPage]);
    }

    if (newPageOrder < currentPageOrder) {
      // right to left transition
      document.documentElement.style.setProperty('--direction', '-100%');
    } else {
      // left to right transition
      document.documentElement.style.setProperty('--direction', '100%');
    }

    context.setSlug(slug || null);
    context.setCurrent(newPage);
  };

  const goBack = () => {
    navigateTo(context.pageHistory[context.pageHistory.length - 2]);
  };

  return {
    navigateTo,
    goBack,
    ...context,
  };
};

export default PageProvider;
